import { useQuery } from '@tanstack/react-query';

import { Skeleton } from '@/components/Skeleton/Skeleton';
import { REPORTS_COPY, TRANSACTIONS_COPY } from '@/features/reports/copy';
import { PeriodControl, ReportFailure } from '@/features/reports/parts';
import { type ReportPeriod } from '@/features/reports/periods';
import { reportTransactionsQuery } from '@/features/reports/queries';
import { TRANSACTIONS_VIEW, type TransactionsView } from '@/features/reports/tabs';
import { MarginView } from '@/features/reports/transactions/MarginView';
import { ProductsView } from '@/features/reports/transactions/ProductsView';
import { ReturnsView } from '@/features/reports/transactions/ReturnsView';

/**
 * Margin, Returns and Products (ALS-86): one period's figures from one request, so the three load
 * and fail as one and moving between them asks nothing again. The period control is the
 * Overview's, and the figures of the period before stay on screen while the next one arrives.
 */
export function PeriodViews({
    view,
    period,
    onPeriodChange,
}: {
    view: Exclude<TransactionsView, 'orders' | 'tax'>;
    period: ReportPeriod;
    onPeriodChange: (period: ReportPeriod) => void;
}) {
    const report = useQuery(reportTransactionsQuery(period));
    const data = report.data;

    return (
        <>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <PeriodControl period={period} onPeriodChange={onPeriodChange} />
                {data ? (
                    <span className="text-muted-foreground text-xs">
                        {REPORTS_COPY.hint(data.current.label, data.previous.label)}
                    </span>
                ) : null}
            </div>

            {report.isPending ? (
                <div
                    aria-busy="true"
                    aria-label="Loading the report"
                    className="flex flex-col gap-(--layout-row-gap)"
                >
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                        {Array.from({ length: 6 }, (_, i) => (
                            <Skeleton key={i} className="h-[84px] rounded-lg" />
                        ))}
                    </div>
                    <Skeleton className="h-[320px] rounded-md" />
                </div>
            ) : report.isError && !data ? (
                <ReportFailure
                    copy={TRANSACTIONS_COPY.failed}
                    onRetry={() => void report.refetch()}
                />
            ) : data ? (
                view === TRANSACTIONS_VIEW.Margin ? (
                    <MarginView report={data} period={period} />
                ) : view === TRANSACTIONS_VIEW.Returns ? (
                    <ReturnsView report={data} period={period} />
                ) : (
                    <ProductsView report={data} period={period} />
                )
            ) : null}
        </>
    );
}
